import { useFormikContext } from 'formik';
import { t } from 'i18next';
import { Prompt } from 'react-router';
import styled from 'styled-components';

import { useLocalize } from '@treams/treams-hooks';
import { Competency, LocalizedString } from '@treams/treams-network';
import { Input, Label, Tags } from '@treams/treams-ui';

import CompetencyLevelPicker from './CompetencyLevelPicker';
import CompetencyPicker from './CompetencyPicker';

export type FormValues = {
	id?: string;
	title: LocalizedString;
	dueDate: Date | undefined;
	competency?: {
		competency?: Competency;
		competencyLevelIndex?: number;
	};
};

const Required = styled.span`
	color: ${({ theme }) => theme.colors.dangerDark};
	font-weight: bold;
`;

const FieldLabel = styled(Label)`
	margin-bottom: 4px;
	display: block;
`;

const FieldSet = styled.div`
	margin-bottom: 16px;
`;

const Inline = styled.div`
	display: inline-block;
`;

const InputError = styled.p`
	color: ${({ theme }) => theme.colors.dangerDark};
	margin-top: 4px;
`;

export default function EditGoalFormFields() {
	const { language, localize } = useLocalize();
	const formik = useFormikContext<FormValues>();

	return (
		<>
			<Prompt when={formik.dirty} message={t('developmentPlan.addGoal.unsavedChangesPrompt')} />

			<FieldSet>
				<FieldLabel>
					{t('developmentPlan.addGoal.competency.fieldLabel')} <Required>*</Required>
				</FieldLabel>
				<Inline>
					<CompetencyPicker />
				</Inline>
				{formik.errors.competency && (
					<InputError>{t('developmentPlan.addGoal.competency.errorLabel')}</InputError>
				)}
			</FieldSet>
			{formik.values.competency && (
				<FieldSet>
					<FieldLabel>
						{t('developmentPlan.addGoal.competencyIndex.fieldLabel')} <Required>*</Required>
					</FieldLabel>
					<Inline>
						<CompetencyLevelPicker />
					</Inline>
				</FieldSet>
			)}

			<FieldSet>
				<FieldLabel>
					{t('developmentPlan.addGoal.title.fieldLabel')} <Required>*</Required>
				</FieldLabel>
				<Input
					autoFocus
					onChange={(text: string) =>
						formik.setFieldValue('title', [{ languageID: language.id, text }])
					}
					value={formik.values.title ? localize(formik.values.title).text : ''}
					flexGrow={1}
				/>
				{formik.errors.title && (
					<InputError>{t('developmentPlan.addGoal.title.errorLabel')}</InputError>
				)}
			</FieldSet>

			<FieldSet>
				<FieldLabel>{t('developmentPlan.addGoal.dueDate.fieldLabel')}</FieldLabel>
				<Inline>
					<Tags.Item.DatePicker
						label={t('developmentPlan.addGoal.dueDate.inputLabel')}
						value={formik.values.dueDate}
						onChange={(date) => formik.setFieldValue('dueDate', date)}
					/>
				</Inline>
			</FieldSet>
		</>
	);
}
